import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";
import { apiErrorText, readApiError, readApiErrorResponse } from "@/lib/api-error";
import { platformFetch } from "@/services/http/platform-fetch";

/** Plan 3's `AccountInviteState` plus `{ userId, email }`, as the detail route sends it. */
export type SchoolInvite = {
  userId: string;
  email: string;
  kind: string;
  at?: string;
  linkValidUntil?: string | null;
  error?: string | null;
  holdReason?: string;
} | null;

const HOLD_KEYS: Record<
  string,
  "waitingSchoolEmail" | "schoolEmailFailing" | "heldUntilMidnightUtc"
> = {
  waiting_school_email: "waitingSchoolEmail",
  school_email_failing: "schoolEmailFailing",
  held_until_midnight_utc: "heldUntilMidnightUtc",
};

/**
 * MT-1 Plan 2 S14 (FR-3 c/d UI): where the first admin's invite stands, and Resend. The 429 says
 * how many minutes to wait (`retryAfterSeconds` from the body, S13c); the 409 says the admin has
 * already set a password.
 */
export function SchoolInviteStatus({
  tenantId,
  invite,
  onResent,
}: {
  tenantId: string;
  invite: SchoolInvite;
  onResent?: () => void;
}) {
  const t = useTranslations("platform.school.invite");
  const tRoot = useTranslations();
  const locale = useLocale();
  const queryClient = useQueryClient();
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const when = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" }) : "";

  function sentences(): string[] {
    if (!invite) return [t("noAdmin")];
    switch (invite.kind) {
      case "password_set":
        return [t("password_set")];
      case "none":
        return [t("none")];
      case "email_queued": {
        const hold = invite.holdReason ? HOLD_KEYS[invite.holdReason] : undefined;
        return [t("email_queued", { at: when(invite.at) }), ...(hold ? [t(hold)] : [])];
      }
      case "email_sent":
        return [
          t("email_sent", { at: when(invite.at) }),
          invite.linkValidUntil
            ? t("linkValidUntil", { until: when(invite.linkValidUntil) })
            : t("linkExpired"),
        ];
      case "email_not_delivered":
        return [
          t("email_not_delivered", { at: when(invite.at) }),
          ...(invite.error ? [invite.error] : []),
        ];
      case "link_printed":
        return [
          t("link_printed", { at: when(invite.at) }),
          invite.linkValidUntil
            ? t("linkValidUntil", { until: when(invite.linkValidUntil) })
            : t("linkExpired"),
        ];
      default:
        return [];
    }
  }

  async function resend() {
    setSending(true);
    setMessage(null);
    setError(null);
    try {
      const res = await platformFetch(`/platform/admin/tenants/${tenantId}/invite/resend`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      if (!res.ok) throw await readApiErrorResponse(res);
      setMessage(t("resent"));
      onResent?.();
      void queryClient.invalidateQueries({ queryKey: ["platform-school", tenantId] });
    } catch (err) {
      const parsed = readApiError(err);
      if (parsed?.code === "RATE_LIMITED") {
        setError(
          parsed.retryAfterSeconds
            ? tRoot("platform.errors.RATE_LIMITED", {
                minutes: Math.max(1, Math.ceil(parsed.retryAfterSeconds / 60)),
              })
            : tRoot("platform.errors.RATE_LIMITED_NO_TIME"),
        );
      } else {
        setError(
          apiErrorText(err, tRoot, "platform.school.invite.resendFailed", {
            namespace: "platform.errors",
          }),
        );
      }
    } finally {
      setSending(false);
    }
  }

  const canResend = invite !== null && invite.kind !== "password_set";

  return (
    <section className="mt-6" aria-labelledby="school-invite-title">
      <h2 id="school-invite-title" className="text-lg font-semibold">
        {t("title")}
      </h2>
      <div className="mt-2 text-sm" data-testid="school-invite-status">
        {invite && <p>{t("for", { email: invite.email })}</p>}
        {sentences().map((s) => (
          <p key={s}>{s}</p>
        ))}
      </div>
      {canResend && (
        <button
          type="button"
          onClick={() => void resend()}
          disabled={sending}
          className="mt-3 rounded-md border border-[var(--border-strong)] px-3 py-2 text-sm"
          data-testid="invite-resend"
        >
          {sending ? t("resending") : t("resend")}
        </button>
      )}
      <div aria-live="polite">{message && <p className="mt-2 text-sm">{message}</p>}</div>
      {error && (
        <p role="alert" className="mt-2 text-sm text-[var(--danger)]">
          {error}
        </p>
      )}
    </section>
  );
}
